import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, ArrowRight, Phone, Search,
  Home, Building2, Briefcase, MapPin, IndianRupee, BedDouble, Car
} from 'lucide-react';
import LocationSearch from '@/components/LocationSearch';
import TurnstileWidget from '@/components/common/TurnstileWidget';

const propertyTypes = [
  { value: 'residential', label: 'Residential', icon: Home },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
  { value: 'agricultural', label: 'Agricultural', icon: Briefcase },
];

const bedroomOptions = ['1 BHK', '2 BHK', '3 BHK', '4 BHK', '5+ BHK', 'Villa', 'Plot'];
const amenities = ['Parking', 'Gym', 'Swimming Pool', 'Garden', 'Lift', 'Security', 'Power Backup', 'Club House'];

const benefits = [
  'Get matched with verified sellers',
  'Receive property suggestions via WhatsApp',
  'No spam - only relevant matches',
  'Priority access to new listings',
  'Dedicated relationship manager',
];

const PostRequirement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [budgetRange, setBudgetRange] = useState([2500000, 10000000]);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    propertyType: 'residential',
    bedroomType: '2 BHK',
    preferredLocations: '',
    selectedAmenities: [],
    timeline: 'within_3_months',
    additionalNotes: ''
  });

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(0)} L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      selectedAmenities: prev.selectedAmenities.includes(amenity)
        ? prev.selectedAmenities.filter(a => a !== amenity)
        : [...prev.selectedAmenities, amenity]
    }));
  };

  // Validation helpers
  const isValidEmail = (email) => {
    return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  // Validate Step 1
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.preferredLocations.trim()) {
      newErrors.preferredLocations = 'Please enter preferred locations';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Step 2
  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    } else {
      toast.error('Please fill in preferred locations');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/', {
        business_line: 'real_estate_buy',
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        city: formData.city,
        message: formData.additionalNotes,
        source: 'post_requirement',
        captcha_token: captchaToken,
        metadata: {
          property_type: formData.propertyType,
          bedroom_type: formData.bedroomType,
          budget_min: budgetRange[0],
          budget_max: budgetRange[1],
          preferred_locations: formData.preferredLocations,
          amenities: formData.selectedAmenities,
          timeline: formData.timeline
        }
      });
      
      toast.success(`Requirement posted successfully! Your Request ID: ${response.data.request_id}`);
      setStep(3);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'CAPTCHA_VALIDATION_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
        setCaptchaToken(null);
      } else {
        toast.error('Failed to submit requirement. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="post-requirement-page">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <FileText className="w-4 h-4" /> Post Your Requirement
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Let Sellers Find You
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Tell us what you&apos;re looking for and we&apos;ll match you with verified sellers. 
                No browsing needed - the right properties come to you.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-primary">500+</p>
                <p className="text-sm text-gray-600">Requirements Posted</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-primary">85%</p>
                <p className="text-sm text-gray-600">Match Rate</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-primary">24hrs</p>
                <p className="text-sm text-gray-600">Avg. Response Time</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                <p className="text-3xl font-bold text-primary">Free</p>
                <p className="text-sm text-gray-600">For Buyers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {step === 3 ? (
            // Success State
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Requirement Posted Successfully!</h2>
              <p className="text-gray-600 mb-8">
                We&apos;ve received your property requirement. Our team will start matching you with relevant sellers immediately.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/buy/properties')} className="rounded-full px-8">
                  Browse Properties <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20just%20posted%20my%20property%20requirement.', '_blank')}
                  className="rounded-full px-8"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Chat with Us
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Progress Steps */}
              <div className="flex items-center justify-center mb-8">
                <div className={`flex items-center ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200'}`}>1</div>
                  <span className="ml-2 font-medium">Requirements</span>
                </div>
                <div className="w-16 h-0.5 bg-gray-200 mx-2" />
                <div className={`flex items-center ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200'}`}>2</div>
                  <span className="ml-2 font-medium">Contact Details</span>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg border">
                {step === 1 ? (
                  // Step 1: Requirements
                  <div className="space-y-6">
                    <h2 className="text-2xl font-semibold mb-6">What are you looking for?</h2>
                    
                    {/* Property Type */}
                    <div>
                      <Label className="text-base font-medium mb-3 block">Property Type</Label>
                      <div className="grid grid-cols-3 gap-4">
                        {propertyTypes.map(type => (
                          <button
                            key={type.value}
                            onClick={() => setFormData({...formData, propertyType: type.value})}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              formData.propertyType === type.value 
                                ? 'border-primary bg-primary/5' 
                                : 'border-gray-200 hover:border-primary/50'
                            }`}
                          >
                            <type.icon className={`w-8 h-8 mx-auto mb-2 ${
                              formData.propertyType === type.value ? 'text-primary' : 'text-gray-400'
                            }`} />
                            <p className="font-medium">{type.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bedroom Type */}
                    <div>
                      <Label className="text-base font-medium mb-3 block">Configuration</Label>
                      <div className="flex flex-wrap gap-2">
                        {bedroomOptions.map(option => (
                          <button
                            key={option}
                            onClick={() => setFormData({...formData, bedroomType: option})}
                            className={`px-4 py-2 rounded-full border-2 transition-all ${
                              formData.bedroomType === option 
                                ? 'border-primary bg-primary text-white' 
                                : 'border-gray-200 hover:border-primary/50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Budget Range */}
                    <div>
                      <div className="flex justify-between mb-3">
                        <Label className="text-base font-medium">Budget Range</Label>
                        <span className="font-semibold text-primary">
                          {formatCurrency(budgetRange[0])} - {formatCurrency(budgetRange[1])}
                        </span>
                      </div>
                      <Slider
                        value={budgetRange}
                        onValueChange={setBudgetRange}
                        min={500000}
                        max={100000000}
                        step={500000}
                        className="mb-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>₹5 L</span>
                        <span>₹10 Cr</span>
                      </div>
                    </div>

                    {/* Preferred Locations */}
                    <div>
                      <Label>Preferred Locations / Areas *</Label>
                      <Input
                        value={formData.preferredLocations}
                        onChange={(e) => {
                          setFormData({...formData, preferredLocations: e.target.value});
                          if (errors.preferredLocations) setErrors({...errors, preferredLocations: ''});
                        }}
                        placeholder="e.g., Gachibowli, Kondapur, Financial District"
                        className={`rounded-xl mt-1 ${errors.preferredLocations ? 'border-red-500' : ''}`}
                      />
                      {errors.preferredLocations && <p className="text-red-500 text-xs mt-1">{errors.preferredLocations}</p>}
                    </div>

                    {/* Amenities */}
                    <div>
                      <Label className="text-base font-medium mb-3 block">Must-have Amenities</Label>
                      <div className="flex flex-wrap gap-2">
                        {amenities.map(amenity => (
                          <button
                            key={amenity}
                            onClick={() => toggleAmenity(amenity)}
                            className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${
                              formData.selectedAmenities.includes(amenity)
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-gray-200 hover:border-primary/50'
                            }`}
                          >
                            {amenity}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div>
                      <Label>When do you plan to buy?</Label>
                      <Select value={formData.timeline} onValueChange={(v) => setFormData({...formData, timeline: v})}>
                        <SelectTrigger className="rounded-xl mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediately">Immediately</SelectItem>
                          <SelectItem value="within_1_month">Within 1 Month</SelectItem>
                          <SelectItem value="within_3_months">Within 3 Months</SelectItem>
                          <SelectItem value="within_6_months">Within 6 Months</SelectItem>
                          <SelectItem value="just_exploring">Just Exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button onClick={handleNextStep} className="w-full rounded-full py-6 text-lg">
                      Continue <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                ) : (
                  // Step 2: Contact Details
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <h2 className="text-2xl font-semibold mb-6">Your Contact Details</h2>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Full Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => {
                            setFormData({...formData, name: e.target.value});
                            if (errors.name) setErrors({...errors, name: ''});
                          }}
                          placeholder="Enter your name"
                          className={`rounded-xl mt-1 ${errors.name ? 'border-red-500' : ''}`}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => {
                            setFormData({...formData, phone: e.target.value});
                            if (errors.phone) setErrors({...errors, phone: ''});
                          }}
                          placeholder="+91 98765 43210"
                          className={`rounded-xl mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({...formData, email: e.target.value});
                            if (errors.email) setErrors({...errors, email: ''});
                          }}
                          placeholder="your@email.com"
                          className={`rounded-xl mt-1 ${errors.email ? 'border-red-500' : ''}`}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <Label>City *</Label>
                        <LocationSearch
                          value={formData.city}
                          onChange={(val) => {
                            setFormData({...formData, city: val});
                            if (errors.city) setErrors({...errors, city: ''});
                          }}
                          placeholder="Search city..."
                          types="city,town"
                          className={`rounded-xl mt-1 ${errors.city ? '[&_input]:border-red-500' : ''}`}
                          data-testid="post-requirement-city"
                        />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>
                    </div>

                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea
                        value={formData.additionalNotes}
                        onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
                        placeholder="Any specific requirements or preferences..."
                        rows={3}
                        className="rounded-xl mt-1"
                      />
                    </div>

                    <TurnstileWidget
                      onVerify={setCaptchaToken}
                      onExpire={() => setCaptchaToken(null)}
                      className="flex justify-center"
                    />

                    <div className="flex gap-4">
                      <Button type="button" variant="outline" onClick={() => setStep(1)} className="rounded-full px-8">
                        Back
                      </Button>
                      <Button type="submit" className="flex-1 rounded-full py-6 text-lg" disabled={loading || !captchaToken}>
                        {loading ? 'Submitting...' : 'Post Requirement'}
                      </Button>
                    </div>
                    
                    <p className="text-center text-sm text-gray-500">
                      By submitting, you agree to receive property suggestions from ZenStudios.
                    </p>
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PostRequirement;
